import _ from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import API from '../../../app/api';
import {BMDestination, BMProwizorkaRefreshData} from '../../../store/reducers/bedManagement';
import {setSnackbar} from '../../../store/reducers/globals';
import {filterBedsByEscalationLevel} from '../../ward/forms/csm/bedRequestWardSelect';
import {mapBeds} from '../bmUtils';

const INIT_HYDRATED = [];

const useBeds = (wardId, wards, bays, bayId)  => {
    const controller = new AbortController();
    const dispatch = useDispatch();

    const shouldUpdate = useSelector(BMProwizorkaRefreshData);
    const isDestination = useSelector(BMDestination);
    const [bedsLoading, setBedsLoading]  = useState(true);
    const [beds, setBeds] = useState([]);
    const [escalationLevel, setEscalationLevel] = useState([]);

    const [returnedData, setReturnedData] = useState(INIT_HYDRATED);

    const fetchBedsByWardId = useCallback((selectedWardId, selectedZone = null) => {
        if (selectedWardId) {
            const escalationLevelWard = _.find(escalationLevel, {wardId: selectedWardId})?.level || 'core';
            API.getAllBeds(selectedWardId, controller.signal)
                .then(bed => {
                    if (bed?.data) {
                        if (selectedZone) {
                            let filtered = _.filter(bed?.data, b => !b.archived && selectedZone?.linkedBeds?.includes(b?.__id));
                            filtered = filterBedsByEscalationLevel(filtered, escalationLevelWard);
                            setBeds(filtered);
                        } else {
                            setBeds(filterBedsByEscalationLevel(bed?.data, escalationLevelWard) || []);
                        }
                    } else {
                        setBeds([]);
                    }
                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting beds for ward id ${selectedWardId}`,
                    }));
                })
                .finally(() => setBedsLoading(false));
        }
    }, [escalationLevel]);
    
    useEffect(() => {
        const allowedToRun = bays && !_.isEmpty(wards) && beds;
        if (allowedToRun) {
            const mappedBeds = mapBeds(beds, bays, wards);
            const filteredByEmptyBayOrWard = _.filter(mappedBeds, b => (b?.bay && b?.ward));
            setReturnedData(filteredByEmptyBayOrWard);
        }
    }, [wards, bays, beds, shouldUpdate])
    
    useEffect(() => {
        if (wardId && !bayId) {
            setBedsLoading(true);
            fetchBedsByWardId(wardId);
        }
    }, [wardId, bayId, shouldUpdate, escalationLevel])
    
    useEffect(() => {
        if (!_.isEmpty(bays) && bayId) {
            setBedsLoading(true);
            const selectedZone = _.find(bays, {__id: bayId});

            fetchBedsByWardId(wardId, selectedZone);
        }
    }, [bays, wardId, bayId, shouldUpdate, isDestination, escalationLevel]);
    
    useEffect(() => {
        API.getEscalationLevels()
            .then(res => setEscalationLevel(res))
            .catch(() => {
                dispatch(setSnackbar({type: 'error', message: 'Unable to retrieve escalation levels.'}));
            });
        return () => controller.abort();
    }, []);
    
    return [beds, bedsLoading, returnedData];
}

export default useBeds;